import React, { useEffect, useMemo, useState } from "react";
import Button from "../../Generic/Button";
import PrimaryButton from "../../Generic/PrimaryButton";
import SuccessButton from "../../Generic/SuccessButton";
import Loader from "../../Generic/Loader";
import { errorNotification } from "../../Generic/Notifications";
import { OfferFormMode, WIZARD_STEPS } from "../constants";
import { useOfferContext } from "../context/OfferContext";
import OffersService from "../Services/OffersService";
import { isEmpty } from "../../util/util";
import BasicInformationStep from "./steps/BasicInformationStep";
import RulesAndActionStep from "./steps/RulesAndActionStep";
import MappingAndTermsStep from "./steps/MappingAndTermsStep";
import TermsAndConditionsStep from "./steps/TermsAndConditionsStep";
import OfferStepIndicator from "./steps/OfferStepIndicator";
import OfferSummary from "./shared/OfferSummary";
import { Page, FormCard, CardHeader, CardTitle, Footer } from "./shared/OfferFormLayout";
import { isOfferDraftDirty } from "../utils/offerDraftDiff";
import {
	validateBasicStep,
	validateRulesStep,
	validateMappingStep,
	validateTermsStep,
} from "../utils/offerValidation";

const OfferFormWizard = ({
	title,
	submitLabel,
	headerExtra = null,
	draft,
	baseline = null,
	mode = OfferFormMode.CREATE,
	onDraftChange,
	onSubmit,
	onCancel,
	submitting = false,
}) => {
	const { offerGroups, setOfferGroupsList } = useOfferContext();
	const [step, setStep] = useState(WIZARD_STEPS.BASIC);
	const [errors, setErrors] = useState({});
	const [loadingGroups, setLoadingGroups] = useState(true);
	const [returnToOverview, setReturnToOverview] = useState(false);

	const isEditMode = mode === OfferFormMode.EDIT;
	const isDirty = useMemo(
		() => (isEditMode ? isOfferDraftDirty(baseline, draft) : true),
		[isEditMode, baseline, draft]
	);

	useEffect(() => {
		fetchGroups();
	}, []);

	async function fetchGroups() {
		setLoadingGroups(true);
		const { data, error } = await OffersService.getOfferGroups();
		if (error) {
			errorNotification(error);
			return;
		}
		setOfferGroupsList(data.items || []);
		setLoadingGroups(false);
	}

	function handleDraftChange(updates, clearErrorKeys = []) {
		onDraftChange(updates);
		if (!clearErrorKeys.length) return;
		setErrors((prev) => {
			const next = { ...prev };
			clearErrorKeys.forEach((key) => {
				delete next[key];
			});
			return next;
		});
	}

	function validateCurrentStep() {
		let stepErrors = {};
		switch (step) {
			case WIZARD_STEPS.BASIC:
				stepErrors = validateBasicStep(draft);
				break;
			case WIZARD_STEPS.RULES:
				stepErrors = validateRulesStep(draft);
				break;
			case WIZARD_STEPS.MAPPING:
				stepErrors = validateMappingStep(draft);
				break;
			case WIZARD_STEPS.TERMS:
				stepErrors = validateTermsStep(draft);
				break;
			default:
				break;
		}
		setErrors(stepErrors);
		return isEmpty(stepErrors);
	}

	function validateAllSteps() {
		const allErrors = {
			...validateBasicStep(draft),
			...validateRulesStep(draft),
			...validateMappingStep(draft),
			...validateTermsStep(draft),
		};
		setErrors(allErrors);
		return isEmpty(allErrors);
	}

	function goNext() {
		if (!validateCurrentStep()) return;

		if (returnToOverview) {
			if (isEditMode && !isDirty) return;
			setReturnToOverview(false);
			setErrors({});
			setStep(WIZARD_STEPS.OVERVIEW);
			return;
		}

		if (step === WIZARD_STEPS.TERMS && isEditMode && !isDirty) return;

		setStep((s) => Math.min(s + 1, WIZARD_STEPS.OVERVIEW));
	}

	function goBack() {
		setErrors({});
		if (returnToOverview) {
			setReturnToOverview(false);
			if (step === WIZARD_STEPS.BASIC) {
				onCancel?.();
				return;
			}
			setStep((s) => s - 1);
			return;
		}
		if (step === WIZARD_STEPS.BASIC) {
			onCancel?.();
			return;
		}
		setStep((s) => s - 1);
	}

	function handleEditSection(targetStep) {
		setErrors({});
		setReturnToOverview(true);
		setStep(targetStep);
	}

	async function handleSubmit() {
		if (!validateAllSteps()) {
			return;
		}
		await onSubmit();
	}

	const forwardDisabled =
		submitting ||
		(returnToOverview && isEditMode && !isDirty) ||
		(step === WIZARD_STEPS.TERMS && !returnToOverview && isEditMode && !isDirty);

	if (loadingGroups) {
		return (
			<Page>
				<Loader />
			</Page>
		);
	}

	return (
		<Page>
			<FormCard>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
					{headerExtra}
				</CardHeader>

				<OfferStepIndicator currentStep={step} />

				{step === WIZARD_STEPS.BASIC && (
					<BasicInformationStep
						draft={draft}
						errors={errors}
						offerGroups={offerGroups}
						onChange={handleDraftChange}
					/>
				)}
				{step === WIZARD_STEPS.RULES && (
					<RulesAndActionStep draft={draft} errors={errors} onChange={handleDraftChange} />
				)}
				{step === WIZARD_STEPS.MAPPING && (
					<MappingAndTermsStep draft={draft} errors={errors} onChange={handleDraftChange} />
				)}
				{step === WIZARD_STEPS.TERMS && (
					<TermsAndConditionsStep
						draft={draft}
						errors={errors}
						onChange={handleDraftChange}
					/>
				)}
				{step === WIZARD_STEPS.OVERVIEW && (
					<OfferSummary
						draft={draft}
						baseline={baseline}
						mode={mode}
						offerGroups={offerGroups}
						onEditSection={handleEditSection}
					/>
				)}

				<Footer>
					<Button onClick={goBack} disabled={submitting}>
						{step === WIZARD_STEPS.BASIC ? "Cancel" : "Back"}
					</Button>
					{step < WIZARD_STEPS.OVERVIEW ? (
						<PrimaryButton onClick={goNext} disabled={forwardDisabled}>
							{returnToOverview ? "Back to Overview" : "Next"}
						</PrimaryButton>
					) : (
						<SuccessButton onClick={handleSubmit} disabled={submitting}>
							{submitLabel}
						</SuccessButton>
					)}
				</Footer>
			</FormCard>
		</Page>
	);
};

export default OfferFormWizard;
